import React from 'react';
import { motion } from 'motion/react';
import { Twitter, Facebook, MessageCircle, Send, Lock } from 'lucide-react';
import { useTranslation } from '../lib/translations';

export const SocialShare: React.FC = () => {
  const { language } = useTranslation();
  const shareUrl = "https://pisti.online";
  const title = language === 'tr'
    ? "Pişti.online - Canlı, Ücretsiz Pişti, Batak, King ve Okey Oyna"
    : "Pişti.online - Play Live & Free Pisti, Batak, King and Okey";

  const shareOptions = [
    { 
      id: 'twitter', 
      name: 'X', 
      icon: Twitter, 
      color: 'text-[#0f1419]', 
      suit1: '♠', suitColor1: 'text-[#8b6b3d]',
      suit2: '♠', suitColor2: 'text-[#8b6b3d]',
      rotate: '-rotate-6'
    },
    { 
      id: 'facebook', 
      name: 'Facebook', 
      icon: Facebook, 
      color: 'text-[#1877F2]',
      suit1: '♥', suitColor1: 'text-[#8b6b3d]',
      suit2: '♥', suitColor2: 'text-[#8b6b3d]',
      rotate: '-rotate-2'
    },
    { 
      id: 'whatsapp', 
      name: 'WhatsApp', 
      icon: MessageCircle, 
      color: 'text-[#25D366]',
      suit1: '♦', suitColor1: 'text-[#8b6b3d]',
      suit2: '♦', suitColor2: 'text-[#8b6b3d]',
      rotate: 'rotate-2'
    },
    { 
      id: 'telegram', 
      name: 'Telegram', 
      icon: Send, 
      color: 'text-[#0088cc]',
      suit1: '♣', suitColor1: 'text-[#8b6b3d]',
      suit2: '♣', suitColor2: 'text-[#8b6b3d]',
      rotate: 'rotate-6'
    },
  ];

  return (
    <div className="w-full max-w-4xl mx-auto my-16 px-4">
      {/* Top Divider with Title */}
      <div className="flex items-center justify-center gap-4 mb-12">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#c09756]/50 max-w-[150px] relative">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rotate-45 bg-[#c09756]" />
        </div>
        <h3 className="text-[#e2b86b] text-xl md:text-2xl font-bold tracking-wide">
          {language === 'tr' ? 'Sosyal Medyada Paylaş' : 'Share on Social Media'}
        </h3>
        <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#c09756]/50 max-w-[150px] relative">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rotate-45 bg-[#c09756]" />
        </div>
      </div>

      {/* Cards Container */}
      <div className="flex items-center justify-center gap-2 md:gap-6 flex-wrap mb-16 perspective-1000">
        {shareOptions.map((option, index) => {
          let url = '';
          switch (option.id) {
            case 'twitter':
              url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title + " " + shareUrl)}`;
              break;
            case 'facebook':
              url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
              break;
            case 'whatsapp':
              url = `https://api.whatsapp.com/send?text=${encodeURIComponent(title + " - " + shareUrl)}`;
              break;
            case 'telegram':
              url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`;
              break;
          }

          return (
            <motion.a
              key={option.id}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={language === 'tr' ? `${option.name} ile Paylaş` : `Share on ${option.name}`}
              title={language === 'tr' ? `${option.name} ile Paylaş` : `Share on ${option.name}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.1, y: -10, rotate: 0 }}
              className={`relative w-24 h-36 md:w-32 md:h-48 bg-[#fdfaf5] rounded-xl shadow-[0_0_15px_rgba(192,151,86,0.3)] border-2 border-[#e8dcb8] flex items-center justify-center ${option.rotate} transition-transform duration-300 group cursor-pointer`}
            >
              {/* Card Content Wrapper */}
              <div className="absolute inset-1 border border-[#e8dcb8]/50 rounded-lg flex items-center justify-center overflow-hidden">
                {/* Corner Suits */}
                <div className={`absolute top-1.5 left-1.5 md:top-2 md:left-2 text-xs md:text-sm ${option.suitColor1}`}>
                  {option.suit1}
                </div>
                <div className={`absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 text-xs md:text-sm ${option.suitColor2} rotate-180`}>
                  {option.suit2}
                </div>
                
                {/* Main Icon */}
                {option.id === 'twitter' ? (
                   <svg viewBox="0 0 24 24" aria-hidden="true" className={`w-10 h-10 md:w-14 md:h-14 ${option.color} group-hover:scale-110 transition-transform duration-300`} fill="currentColor">
                     <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.008 5.925H5.064z"></path>
                   </svg>
                ) : (
                  <option.icon className={`w-10 h-10 md:w-14 md:h-14 ${option.color} group-hover:scale-110 transition-transform duration-300`} strokeWidth={option.id === 'facebook' ? 1.5 : 2} fill={option.id === 'facebook' ? "currentColor" : "none"} />
                )}
              </div>
            </motion.a>
          );
        })}
      </div>

      {/* Bottom Divider */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <div className="h-px w-full max-w-[200px] md:max-w-xs bg-gradient-to-r from-transparent to-[#c09756]/50 relative">
           <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#c09756]" />
        </div>
        <div className="text-[#c09756] text-xl mt-1">
          ♠
        </div>
        <div className="h-px w-full max-w-[200px] md:max-w-xs bg-gradient-to-l from-transparent to-[#c09756]/50 relative">
           <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#c09756]" />
        </div>
      </div>

      {/* Footer Disclaimer */}
      <div className="flex items-center justify-center gap-2 text-slate-300 text-xs mt-6">
        <Lock className="w-3 h-3" />
        <span>{language === 'tr' ? 'Sosyal medya markaları ilgili sahiplerine aittir.' : 'Social media brands belong to their respective owners.'}</span>
      </div>
    </div>
  );
};

